import * as React from "react";

import { useAreaExplore } from "@/pages/areas/hooks/use-area-explore";
import type { AreaListItem } from "@/types/area";

/** Parts in display order: `partOrder` first, parts with none last, then by name. */
const byPartOrder = (a: AreaListItem, b: AreaListItem): number =>
  (a.partOrder ?? Number.MAX_SAFE_INTEGER) -
    (b.partOrder ?? Number.MAX_SAFE_INTEGER) || a.name.localeCompare(b.name);

/**
 * The parts of one city, or of every city when none is picked: one `kind:=part` explore query,
 * 250 rows at most. The list's Part column maps names by id from it and the Part filter and the
 * reorder dialog read the picked city's.
 */
export const useAreaParts = (cityKey: string | undefined) => {
  const { rows, isLoading, error } = useAreaExplore({
    filters: [
      { field: "kind", operator: "eq", value: "part" },
      { field: "cityKey", operator: "eq", value: cityKey },
    ],
  });
  const parts = React.useMemo(() => [...rows].sort(byPartOrder), [rows]);
  const partNames = React.useMemo(
    () => new Map(parts.map((p) => [p.id, p.name])),
    [parts],
  );
  return { parts, partNames, isLoading, error };
};
